import React from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, MessageSquareText } from 'lucide-react';
import useAccess from '../../hooks/useAccess.jsx';
import { ACCESS_PERMISSION_KEYS } from '../../lib/accessControl.js';
import { buildBillingClientExplanation } from '../../utils/billingClientExplanation.js';
import './BillingClientExplanationView.css';

const ExplanationView = React.lazy(() => import('./BillingClientExplanationView.jsx'));

export function BillingDocumentError({ title = 'Cette explication ne peut pas être affichée.' }) {
  return <div role="alert" className="billing-client-explanation-state">
    <h2>{title}</h2>
    <p>Revenez à la facturation pour consulter les prestations et réessayer.</p>
  </div>;
}

export class BillingDocumentBoundary extends React.Component {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() { return this.state.failed ? <BillingDocumentError title={this.props.title} /> : this.props.children; }
}

/** Page plein écran au-dessus de la facturation ; à l'impression, elle seule reste. */
export function BillingDocumentDialog({ label, backLabel = 'Retour à la facturation', note = '', actions = null, onClose, children, targetDocument = document }) {
  const dialogRef = React.useRef(null);
  const backRef = React.useRef(null);
  React.useEffect(() => {
    const dialog = dialogRef.current;
    const opener = targetDocument.activeElement;
    const previousOverflow = targetDocument.body.style.overflow;
    dialog.showModal();
    targetDocument.body.style.overflow = 'hidden';
    backRef.current?.focus();
    return () => {
      dialog.close();
      targetDocument.body.style.overflow = previousOverflow;
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, [targetDocument]);

  return createPortal(<dialog ref={dialogRef} className="billing-client-explanation-dialog" aria-label={label}
    // Ouverte depuis une fenêtre de facturation, ses touches (Échap, Tab) ne remontent pas jusqu'à elle.
    onKeyDown={(event) => event.stopPropagation()}
    onCancel={(event) => { event.preventDefault(); onClose(); }}>
    <div className="billing-client-explanation-toolbar">
      <button ref={backRef} type="button" onClick={onClose}
        className="inline-flex min-h-9 items-center gap-2 rounded-[4px] border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-2 focus-visible:outline-accent">
        <ArrowLeft size={16} aria-hidden="true" /> {backLabel}
      </button>
      {note ? <span className="ml-auto text-xs text-slate-500">{note}</span> : null}
      {actions}
    </div>
    {children}
  </dialog>, targetDocument.body);
}

function ExplanationDialog({ snapshot, onClose }) {
  return <BillingDocumentDialog label="Explication des prestations pour le client" note="Présentation des prestations" onClose={onClose}>
    <BillingDocumentBoundary>
      {snapshot.error ? <BillingDocumentError /> : <React.Suspense fallback={<p role="status" className="billing-client-explanation-state">Préparation de l’explication…</p>}>
        <ExplanationView model={snapshot.model} statement={snapshot.statement} />
      </React.Suspense>}
    </BillingDocumentBoundary>
  </BillingDocumentDialog>;
}

export function useBillingClientExplanation() {
  const { hasPermission, isAuthError, isReady } = useAccess();
  const canRead = !isAuthError && isReady !== false && hasPermission(ACCESS_PERMISSION_KEYS.billingRead);
  const [snapshot, setSnapshot] = React.useState(null);
  React.useEffect(() => { if (!canRead) setSnapshot(null); }, [canRead]);
  // statement : totaux d'un relevé de transport ; l'explication affiche alors le total du relevé, surtaxe comprise.
  function openExplanation(items, statement = null) {
    if (!canRead) return;
    // Sans prestation (lecture d'un relevé échouée), la fenêtre s'ouvre sur son message d'erreur.
    try { setSnapshot(items?.length ? { model: buildBillingClientExplanation(items), statement } : { error: true }); }
    catch { setSnapshot({ error: true }); }
  }
  return {
    canRead,
    openExplanation,
    explanationDialog: canRead && snapshot ? <ExplanationDialog snapshot={snapshot} onClose={() => setSnapshot(null)} /> : null,
  };
}

export default function BillingClientExplanationLauncher({ items, statement = null, onOpen }) {
  const { canRead, openExplanation, explanationDialog } = useBillingClientExplanation();
  if (!canRead) return null;
  return <>
    <button type="button" onClick={() => onOpen ? onOpen() : openExplanation(items, statement)} disabled={!onOpen && !items?.length}
      className="inline-flex min-h-8 shrink-0 items-center justify-center gap-1.5 rounded-[4px] border border-accent bg-accent px-2.5 py-1 text-xs font-bold text-white hover:border-accent-strong hover:bg-accent-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100 disabled:shadow-none">
      <MessageSquareText size={14} aria-hidden="true" /> Expliquer au client
    </button>
    {explanationDialog}
  </>;
}
